import { Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'

export default function ClevelandOverviewPage() {
  return (
    <div className="mx-auto max-w-4xl space-y-8 p-4 py-12">
      <Link to="/resources" className="text-primary hover:underline">
        &larr; Back to Resources
      </Link>
      <h1 className="text-fluid-h2 text-navy">Why Cleveland (Most Coastal Investors Skip It)</h1>

      <Card>
        <CardContent className="p-6">
          <p className="text-lg">
            Cleveland is one of the strongest cash-flow markets in the country for small multifamily investing. The city
            consistently ranks in the top 10 for rental yield because of affordable purchase prices, stable rental
            demand, and a diversified economy that most coastal investors overlook.
          </p>
        </CardContent>
      </Card>

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">Why Cleveland Works for Cash Flow</h2>

        <div className="grid gap-4 md:grid-cols-2">
          {[
            {
              metric: 'Median Home Price',
              value: '~$110K',
              context: 'Compared to $400K+ nationally. Entry points are 3-4x lower than coastal markets.',
            },
            {
              metric: 'Median Rent (2BR)',
              value: '~$950-$1,200',
              context: 'Rent-to-price ratios of 0.8-1.2% monthly. Many markets are 0.3-0.5%.',
            },
            {
              metric: 'Population (Metro)',
              value: '~2M',
              context: 'Stable metro population with modest growth. Not a boom-bust cycle market.',
            },
            {
              metric: 'Major Employers',
              value: 'Healthcare, Manufacturing, Tech',
              context: 'Cleveland Clinic, University Hospitals, NASA Glenn, Progressive, KeyBank.',
            },
          ].map((item) => (
            <Card key={item.metric}>
              <CardContent className="p-5">
                <div className="text-muted-foreground text-xs">{item.metric}</div>
                <div className="mb-1 text-xl font-bold text-primary">{item.value}</div>
                <p className="text-muted-foreground text-sm">{item.context}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <Separator />

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">Target Submarkets</h2>
        <p className="text-muted-foreground">
          Cleveland is a neighborhood-by-neighborhood market. Two streets apart can have completely different economics.
          We focus on working-class neighborhoods with strong rental demand, stable occupancy, and improving trends.
        </p>

        <div className="space-y-3">
          {[
            {
              area: 'West Boulevard / Cudell',
              profile:
                'Our first acquisition market. Affordable duplexes, strong rental demand from working families, close to transit. Purchase prices: $120K-$200K for duplexes.',
              status: 'Active',
            },
            {
              area: 'Detroit Shoreway',
              profile:
                'Gentrifying west side neighborhood near Gordon Square arts district. Higher purchase prices but strong rent growth trajectory. Good for value-add.',
              status: 'Monitoring',
            },
            {
              area: 'Lakewood',
              profile:
                'Inner-ring suburb west of Cleveland. Walkable, good schools, strong tenant demand. Higher price point but lower risk. Mostly duplexes and triplexes.',
              status: 'Monitoring',
            },
            {
              area: 'Old Brooklyn',
              profile:
                'South side neighborhood with affordable multifamily stock. Stable blue-collar tenant base. Watch for deferred maintenance on older buildings.',
              status: 'Researching',
            },
            {
              area: 'Tremont',
              profile:
                'Near west side, significant investment in recent years. Higher price per unit but strong demand from young professionals. More competitive.',
              status: 'Watching',
            },
          ].map((area) => (
            <Card key={area.area}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold">{area.area}</h3>
                    <p className="text-muted-foreground mt-1 text-sm">{area.profile}</p>
                  </div>
                  <span
                    className={`shrink-0 rounded px-2 py-1 text-xs font-medium ${
                      area.status === 'Active'
                        ? 'bg-success/10 text-success'
                        : area.status === 'Monitoring'
                          ? 'bg-primary/20 text-primary'
                          : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {area.status}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <Separator />

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">Cleveland vs. Other Markets</h2>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-muted border-b">
                <th className="p-3 text-left font-semibold">Metric</th>
                <th className="p-3 text-left font-semibold text-primary">Cleveland</th>
                <th className="p-3 text-left font-semibold">Austin</th>
                <th className="p-3 text-left font-semibold">Nashville</th>
              </tr>
            </thead>
            <tbody className="text-muted-foreground">
              <tr className="border-muted/50 border-b">
                <td className="p-3">Duplex Price</td>
                <td className="p-3 font-medium text-primary">$120-$200K</td>
                <td className="p-3">$400-$600K</td>
                <td className="p-3">$350-$500K</td>
              </tr>
              <tr className="border-muted/50 border-b">
                <td className="p-3">Monthly Rent (2BR)</td>
                <td className="p-3 font-medium text-primary">$950-$1,200</td>
                <td className="p-3">$1,400-$1,800</td>
                <td className="p-3">$1,300-$1,700</td>
              </tr>
              <tr className="border-muted/50 border-b">
                <td className="p-3">Rent-to-Price</td>
                <td className="p-3 font-medium text-primary">0.8-1.2%</td>
                <td className="p-3">0.3-0.4%</td>
                <td className="p-3">0.3-0.5%</td>
              </tr>
              <tr className="border-muted/50 border-b">
                <td className="p-3">Cap Rate</td>
                <td className="p-3 font-medium text-primary">8-12%</td>
                <td className="p-3">3-5%</td>
                <td className="p-3">4-6%</td>
              </tr>
              <tr>
                <td className="p-3">Cash-on-Cash (typical)</td>
                <td className="p-3 font-medium text-primary">8-12%</td>
                <td className="p-3">2-4%</td>
                <td className="p-3">3-5%</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-muted-foreground text-sm">
          Hot markets offer appreciation potential. Cleveland offers cash flow from day one. Both are valid strategies,
          but our investors want income now, not a bet on future price growth.
        </p>
      </section>

      <Separator />

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">Risks to Know</h2>
        <p className="text-muted-foreground">
          Cleveland is not without risks. Being clear-eyed about them is part of responsible investing.
        </p>

        <div className="space-y-2">
          {[
            {
              risk: 'Older housing stock',
              detail:
                'Most multifamily was built pre-1960. Expect higher maintenance costs, lead paint considerations, and potential for deferred maintenance from previous owners.',
            },
            {
              risk: 'Neighborhood variability',
              detail:
                'A few blocks can make a major difference in tenant quality and rental rates. Deep local knowledge matters more here than in homogeneous suburban markets.',
            },
            {
              risk: 'Limited appreciation',
              detail:
                "Cleveland grows value slowly. If you're counting on 10%/year appreciation, this is the wrong market. We target 2-3% annual appreciation as a bonus, not the thesis.",
            },
            {
              risk: 'Property tax reassessment',
              detail:
                'Cuyahoga County reassesses every 3 years. A purchase at market value can trigger a reassessment that increases your tax burden. Underwrite for it.',
            },
            {
              risk: 'Tenant protections',
              detail:
                'Ohio is generally landlord-friendly compared to coastal states, but Cleveland has some local ordinances. Know them before you invest.',
            },
          ].map((item) => (
            <Card key={item.risk}>
              <CardContent className="p-4">
                <p className="font-semibold">{item.risk}</p>
                <p className="text-muted-foreground mt-1 text-sm">{item.detail}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <Card>
        <CardContent className="p-6 text-center">
          <p className="mb-2 text-lg font-semibold">See how we apply this in practice</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/track-record" className="text-primary hover:underline">
              View Our Portfolio &rarr;
            </Link>
            <Link to="/resources/deal-criteria" className="text-primary hover:underline">
              Our Deal Criteria &rarr;
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
